import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Save, Camera, Image as ImageIcon, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function PhotographerCreateEvent() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [series, setSeries] = useState("");
  const [classes, setClasses] = useState("");
  const [heats, setHeats] = useState("");
  const [saving, setSaving] = useState(false);
  const [createdEvent, setCreatedEvent] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        date,
        location: location.trim(),
        series: series.trim() || null,
        classes: classes ? classes.split(",").map((s) => s.trim()).filter(Boolean) : [],
        heats: heats ? heats.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      const r = await api.post("/photographers/me/events", payload);
      setCreatedEvent(r.data);
      toast.success("Eventet är skapat! Lägg gärna till en omslagsbild.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa event");
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file || !createdEvent) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/events/${createdEvent.id}/cover-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCreatedEvent({ ...createdEvent, cover_image_url: r.data.cover_image_url });
      toast.success("Omslagsbild uppladdad");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ladda upp");
    } finally {
      setUploading(false);
    }
  };

  const removeCover = async () => {
    if (!createdEvent) return;
    if (!window.confirm("Ta bort omslagsbilden?")) return;
    await api.delete(`/events/${createdEvent.id}/cover-image`);
    setCreatedEvent({ ...createdEvent, cover_image_url: "" });
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/photographer/dashboard" className="text-brand hover:underline text-sm mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Tillbaka
        </Link>
        <p className="label-overline mb-2">Nytt event</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-2">
          Skapa nytt event
        </h1>
        <p className="text-white/60 mb-6 text-sm">
          Saknas ditt event? Skapa det här. Admin granskar och publicerar inom 24 timmar — du kan ladda upp foton direkt under tiden, men det blir publikt synligt först när det är godkänt.
        </p>

        {createdEvent ? (
          <div className="card-surface p-6 space-y-5" data-testid="event-created">
            <div className="flex items-center gap-2 text-brand">
              <CheckCircle2 className="w-6 h-6" />
              <p className="font-display font-bold uppercase">Eventet är skapat</p>
            </div>
            <p className="text-white/70 text-sm">
              <span className="text-white">{createdEvent.name}</span> · {createdEvent.date} · {createdEvent.location}
              <br />
              Status: <span className="text-yellow-400 uppercase font-bold">Väntar på godkännande</span>
            </p>

            <div>
              <p className="label-overline mb-2">Omslagsbild (frivilligt)</p>
              <div className="aspect-[16/9] bg-[#1a1a1a] border border-white/10 overflow-hidden">
                {createdEvent.cover_image_url ? (
                  <img
                    src={resolveImageUrl(createdEvent.cover_image_url)}
                    alt="Cover"
                    className="w-full h-full object-cover"
                    data-testid="created-cover-preview"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-white/20" />
                  </div>
                )}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadCover(e.target.files?.[0])}
                data-testid="created-cover-input"
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="created-cover-upload"
                >
                  <Camera className="w-4 h-4" />
                  {uploading ? "Laddar upp..." : createdEvent.cover_image_url ? "Byt bild" : "Ladda upp"}
                </button>
                {createdEvent.cover_image_url && (
                  <button onClick={removeCover} className="btn-secondary px-3 hover:bg-red-900/40">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </div>
              <p className="text-xs text-white/40 mt-2">JPG/PNG · max 10 MB · skalas till 1600px</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => navigate("/photographer/upload")}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="cover-go-upload"
              >
                Gå till uppladdning av foton
              </button>
              <button
                onClick={() => navigate("/photographer/dashboard")}
                className="btn-secondary"
                data-testid="cover-go-dashboard"
              >
                Klar — till dashboard
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-surface p-6 space-y-4" data-testid="create-event-form">
            <div>
              <label className="label-overline block mb-1">Namn *</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Karlströms Cup Arlanda"
                className="input-field"
                data-testid="event-name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-1">Datum *</label>
                <input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="input-field"
                  data-testid="event-date"
                />
              </div>
              <div>
                <label className="label-overline block mb-1">Plats *</label>
                <input
                  required
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Ex: Arlanda, Sverige"
                  className="input-field"
                  data-testid="event-location"
                />
              </div>
            </div>

            <div>
              <label className="label-overline block mb-1">Serie / förbund (frivilligt)</label>
              <input
                value={series}
                onChange={(e) => setSeries(e.target.value)}
                placeholder="Ex: SM, Karlströms Cup, Klassiker"
                className="input-field"
                data-testid="event-series"
              />
            </div>

            <div>
              <label className="label-overline block mb-1">Klasser (komma-separerat)</label>
              <input
                value={classes}
                onChange={(e) => setClasses(e.target.value)}
                placeholder="Pro, Senior, Junior, Veteran, Damer"
                className="input-field"
                data-testid="event-classes"
              />
              <p className="text-xs text-white/40 mt-1">Underlättar för fotografer att tagga foton per klass.</p>
            </div>

            <div>
              <label className="label-overline block mb-1">Heat (frivilligt — sällan i enduro)</label>
              <input
                value={heats}
                onChange={(e) => setHeats(e.target.value)}
                placeholder="Heat 1, Heat 2"
                className="input-field"
                data-testid="event-heats"
              />
            </div>

            <p className="text-xs text-white/40">Du kan ladda upp en omslagsbild i nästa steg.</p>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="create-event-submit">
                <Save className="w-4 h-4" />
                {saving ? "Sparar..." : "Skapa event"}
              </button>
              <Link to="/photographer/dashboard" className="btn-secondary">Avbryt</Link>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
